import {
  Announcements,
  closestCenter,
  DndContext,
  DragEndEvent,
  Modifier,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Box, Checkbox, IconButton, SxProps, Theme, Typography } from '@mui/material';
import { KeyboardEvent, useCallback, useMemo, useState } from 'react';
import { clamp } from 'remeda';
import { DynamicIcon } from 'src/components';
import { CustomViewAttribute } from 'src/graphql/entities/custom-view';
import {
  attributeRowSx,
  attributeToggleSx,
  getDesktopGripSx,
  liveRegionSx,
  touchHandleSx,
} from 'src/pages/CustomViews/custom-views-form-view/draggable-list.styles';
import { capitalizeString } from 'src/utils/strings';
import { useScreenDimensions } from 'src/utils/system';

type MoveDirection = -1 | 1;

const MOVE_DIRECTION_BY_KEY: Partial<Record<string, MoveDirection>> = {
  ArrowUp: -1,
  ArrowDown: 1,
};

// Moves are announced through our own live region so drag and arrow keys read the same.
const SILENT_DRAG_ANNOUNCEMENTS: Announcements = {
  onDragStart: () => undefined,
  onDragOver: () => undefined,
  onDragEnd: () => undefined,
  onDragCancel: () => undefined,
};

const SCREEN_READER_INSTRUCTIONS = {
  draggable: 'To reorder, press the up or down arrow key, or drag the handle.',
};

// A row dragged sideways or past the list widens and lengthens the scrollable popover under it.
const restrictToListColumn: Modifier = ({
  transform,
  draggingNodeRect,
  containerNodeRect,
}) => {
  if (!draggingNodeRect || !containerNodeRect) {
    return { ...transform, x: 0 };
  }

  return {
    ...transform,
    x: 0,
    y: clamp(transform.y, {
      min: containerNodeRect.top - draggingNodeRect.top,
      max: containerNodeRect.bottom - draggingNodeRect.bottom,
    }),
  };
};

const DRAG_MODIFIERS = [restrictToListColumn];

// iOS Safari still rubber-bands a scroller at its top edge under a `touch-action: none` handle,
// so a downward drag also scrolls. Cancelling touchmove stops it, but only from a non-passive
// native listener: React registers touchmove as passive, where preventDefault is ignored.
const blockTouchScroll = (node: HTMLElement | null) => {
  if (!node) {
    return undefined;
  }

  const preventScroll = (event: TouchEvent) => event.preventDefault();

  node.addEventListener('touchmove', preventScroll, { passive: false });

  return () => node.removeEventListener('touchmove', preventScroll);
};

const DraggableItem = ({
  id,
  column,
  isTouchLayout,
  onSelect,
  onMove,
}: {
  id: string;
  column: CustomViewAttribute;
  isTouchLayout: boolean;
  onSelect: (column: CustomViewAttribute) => void;
  onMove: (column: CustomViewAttribute, direction: MoveDirection) => void;
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable(
    { id },
  );

  const label = capitalizeString(column.attributeLabel);

  const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    const direction = MOVE_DIRECTION_BY_KEY[event.key];

    if (!direction) {
      return;
    }

    event.preventDefault();
    onMove(column, direction);
  };

  const handleProps = {
    ...attributes,
    ...listeners,
    'aria-label': `Reorder ${label}`,
    onKeyDown: handleKeyDown,
  };

  const sx: SxProps<Theme> = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.45 : 1,
    position: 'relative',
    zIndex: isDragging ? 1 : undefined,
  };

  return (
    <Box ref={setNodeRef} sx={sx}>
      <Box sx={attributeRowSx}>
        {isTouchLayout ? null : (
          <Box ref={blockTouchScroll} {...handleProps} sx={getDesktopGripSx(isDragging)}>
            <DynamicIcon name="grip-vertical" library="lucide" size={15} />
          </Box>
        )}

        <Box component="label" sx={attributeToggleSx}>
          <Checkbox
            checked
            size="small"
            onChange={() => onSelect(column)}
            inputProps={{ 'aria-label': `Hide ${label}` }}
            sx={{ p: 0 }}
          />
          <Typography variant="body2" noWrap minWidth={0}>
            {label}
          </Typography>
        </Box>

        <Typography variant="caption" color="text.secondary" noWrap flexShrink={0}>
          {capitalizeString(column.group)}
        </Typography>

        {isTouchLayout ? (
          <IconButton ref={blockTouchScroll} disableRipple {...handleProps} sx={touchHandleSx}>
            <DynamicIcon name="equal" library="lucide" size={16} />
          </IconButton>
        ) : null}
      </Box>
    </Box>
  );
};

export const DraggableList = ({
  selectedColumns,
  onSelect,
  onReorder,
}: {
  selectedColumns: CustomViewAttribute[];
  onSelect: (column: CustomViewAttribute) => void;
  onReorder: (columns: CustomViewAttribute[]) => void;
}) => {
  const { isMobile, isTouchDevice } = useScreenDimensions();
  const isTouchLayout = isMobile || isTouchDevice;

  const [moveAnnouncement, setMoveAnnouncement] = useState('');

  const moveColumn = useCallback(
    (fromIndex: number, toIndex: number) => {
      if (fromIndex === toIndex || toIndex < 0 || toIndex >= selectedColumns.length) {
        return;
      }

      const movedLabel = capitalizeString(selectedColumns[fromIndex].attributeLabel);

      onReorder(arrayMove(selectedColumns, fromIndex, toIndex));
      setMoveAnnouncement(
        `${movedLabel} moved to position ${toIndex + 1} of ${selectedColumns.length}`,
      );
    },
    [selectedColumns, onReorder],
  );

  const handleDragEnd = useCallback(
    ({ active, over }: DragEndEvent) => {
      if (!over || active.id === over.id) {
        return;
      }

      moveColumn(
        selectedColumns.findIndex((col) => col.attribute === active.id),
        selectedColumns.findIndex((col) => col.attribute === over.id),
      );
    },
    [selectedColumns, moveColumn],
  );

  const handleMove = useCallback(
    (column: CustomViewAttribute, direction: MoveDirection) => {
      const fromIndex = selectedColumns.findIndex((col) => col.attribute === column.attribute);

      moveColumn(fromIndex, fromIndex + direction);
    },
    [selectedColumns, moveColumn],
  );

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
  );

  const attributeList = useMemo(
    () => selectedColumns.map((col) => col.attribute),
    [selectedColumns],
  );

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      modifiers={DRAG_MODIFIERS}
      onDragEnd={handleDragEnd}
      accessibility={{
        announcements: SILENT_DRAG_ANNOUNCEMENTS,
        screenReaderInstructions: SCREEN_READER_INSTRUCTIONS,
      }}
    >
      <SortableContext items={attributeList} strategy={verticalListSortingStrategy}>
        <Box>
          {selectedColumns.map((column) => (
            <DraggableItem
              key={column.attribute}
              id={column.attribute}
              column={column}
              isTouchLayout={isTouchLayout}
              onSelect={onSelect}
              onMove={handleMove}
            />
          ))}
        </Box>
      </SortableContext>

      <Box aria-live="polite" sx={liveRegionSx}>
        {moveAnnouncement}
      </Box>
    </DndContext>
  );
};
